/**
 * Recorte da foto do Meu Cartão: remove o fundo (fica só a pessoa) e aplica o efeito 3D
 * (contorno dourado + sombra) direto nos pixels — assim o PNG/PDF exportado pelo html2canvas
 * sai igual à tela (html2canvas ignora CSS filter/drop-shadow).
 *
 * Remoção de fundo: MediaPipe Image Segmenter (modelo selfie_segmenter, Apache 2.0), rodando
 * NO NAVEGADOR — a foto não vai para nenhum serviço de terceiros. A biblioteca (ESM moderno) é
 * carregada sob demanda do CDN por um <script type="module"> injetado, para não passar pelo
 * webpack 4 do projeto; só baixa quando alguém envia foto.
 */

const MP_VERSAO = "0.10.14";
const MP_BASE = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MP_VERSAO}`;
const MODELO =
  "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite";
const EVENTO = "st-vision-pronto";
const TIMEOUT_MS = 45000;

let carregandoVision = null;
let segmenterPromise = null;

function carregarVision() {
  if (window.__stVision) return Promise.resolve(window.__stVision);
  if (carregandoVision) return carregandoVision;

  carregandoVision = new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("timeout")), TIMEOUT_MS);
    window.addEventListener(
      EVENTO,
      () => {
        clearTimeout(timer);
        if (window.__stVision) resolve(window.__stVision);
        else reject(new Error("vision indisponível"));
      },
      { once: true }
    );
    const s = document.createElement("script");
    s.type = "module";
    s.textContent =
      `import("${MP_BASE}/vision_bundle.mjs")` +
      `.then(function (m) { window.__stVision = m; })` +
      `.catch(function () {})` +
      `.finally(function () { window.dispatchEvent(new Event("${EVENTO}")); });`;
    document.head.appendChild(s);
  }).catch((e) => {
    carregandoVision = null;
    throw e;
  });
  return carregandoVision;
}

function obterSegmenter() {
  if (segmenterPromise) return segmenterPromise;
  segmenterPromise = carregarVision()
    .then(({ ImageSegmenter, FilesetResolver }) =>
      FilesetResolver.forVisionTasks(`${MP_BASE}/wasm`).then((fileset) =>
        ImageSegmenter.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: MODELO, delegate: "CPU" },
          runningMode: "IMAGE",
          outputCategoryMask: false,
          outputConfidenceMasks: true,
        })
      )
    )
    .catch((e) => {
      segmenterPromise = null;
      throw e;
    });
  return segmenterPromise;
}

function novoCanvas(w, h) {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

function suavizar(p) {
  // smoothstep entre 0,35 e 0,75: borda macia sem "halo" do fundo.
  const t = Math.min(1, Math.max(0, (p - 0.35) / 0.4));
  return t * t * (3 - 2 * t);
}

/**
 * Remove o fundo. Devolve um canvas do mesmo tamanho com fundo transparente.
 * Lança erro se a biblioteca não carregar ou não encontrar uma pessoa.
 */
export async function removerFundo(origem) {
  const segmenter = await obterSegmenter();
  const resultado = segmenter.segment(origem);
  const mascaras = (resultado && resultado.confidenceMasks) || [];
  if (!mascaras.length) {
    if (resultado && resultado.close) resultado.close();
    throw new Error("sem máscara");
  }

  const m = mascaras[0];
  const w = m.width;
  const h = m.height;
  const dados = Float32Array.from(m.getAsFloat32Array());
  if (resultado.close) resultado.close();

  // Qual lado é a pessoa? Compara o centro com as bordas (independe da convenção do modelo).
  let centro = 0;
  let nc = 0;
  let borda = 0;
  let nb = 0;
  for (let y = 0; y < h; y += 4) {
    for (let x = 0; x < w; x += 4) {
      const v = dados[y * w + x];
      const noCentro = x > w * 0.3 && x < w * 0.7 && y > h * 0.25 && y < h * 0.75;
      const naBorda = x < w * 0.08 || x > w * 0.92 || y < h * 0.08;
      if (noCentro) {
        centro += v;
        nc++;
      } else if (naBorda) {
        borda += v;
        nb++;
      }
    }
  }
  const inverter = nc && nb && centro / nc < borda / nb;

  let pessoa = 0;
  const mascara = novoCanvas(w, h);
  const mctx = mascara.getContext("2d");
  const img = mctx.createImageData(w, h);
  for (let i = 0; i < w * h; i++) {
    const p = suavizar(inverter ? 1 - dados[i] : dados[i]);
    if (p > 0.5) pessoa++;
    img.data[i * 4 + 3] = Math.round(p * 255);
  }
  mctx.putImageData(img, 0, 0);

  if (pessoa < w * h * 0.04) throw new Error("pessoa não encontrada");

  const saida = novoCanvas(origem.width, origem.height);
  const ctx = saida.getContext("2d");
  ctx.drawImage(origem, 0, 0);
  ctx.globalCompositeOperation = "destination-in";
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(mascara, 0, 0, saida.width, saida.height);
  ctx.globalCompositeOperation = "source-over";
  return saida;
}

/**
 * Efeito 3D sobre o recorte: contorno dourado + sombra projetada. A base (peito) fica rente à
 * borda de baixo para a pessoa "sair" do painel do cartão.
 */
export function aplicarEfeito3d(recorte) {
  const W = recorte.width;
  const H = recorte.height;
  const pad = Math.round(W * 0.06);
  const saida = novoCanvas(W + pad * 2, H + pad);
  const ctx = saida.getContext("2d");

  // Silhueta dourada (para o contorno).
  const silhueta = novoCanvas(W, H);
  const sctx = silhueta.getContext("2d");
  sctx.drawImage(recorte, 0, 0);
  sctx.globalCompositeOperation = "source-in";
  sctx.fillStyle = "#f7d774";
  sctx.fillRect(0, 0, W, H);

  const raio = Math.max(3, Math.round(W * 0.009));
  for (let a = 0; a < 360; a += 30) {
    const r = (a * Math.PI) / 180;
    ctx.drawImage(silhueta, pad + Math.cos(r) * raio, pad + Math.sin(r) * raio);
  }

  ctx.save();
  ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
  ctx.shadowBlur = Math.round(W * 0.045);
  ctx.shadowOffsetY = Math.round(W * 0.02);
  ctx.drawImage(recorte, pad, pad);
  ctx.restore();

  return saida;
}

export function canvasParaBlob(canvas, tipo = "image/png", qualidade) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob"))), tipo, qualidade);
  });
}
